(function() {
  'use strict';

  /* =======================================================================
     SbGallery — Image lightbox with crossfade, autoplay, swipe
     ======================================================================= */
  var SbGallery = {
    images: [],
    index: 0,
    lightbox: null,
    viewport: null,
    img: null,
    counter: null,
    playing: false,
    timer: null,
    AUTOPLAY_MS: 3500,
    cursorEl: null,

    /* ─── Init ─── */
    init: function() {
      this.collectImages();
      if (this.images.length === 0) return;
      this.initCursor();
      this.initClicks();
    },

    /* ─── Collect all article images (hero + body) ─── */
    collectImages: function() {
      var self = this;
      self.images = [];
      var imgs = document.querySelectorAll('.blog-article__hero img, .blog-article__body img');
      var idx = 0;

      imgs.forEach(function(img) {
        /* Skip product embeds and carousel cards. The in-body product
           carousel renders inside .blog-article__body, so its <img>
           elements match this collector. Without this exclusion, the
           gallery attached a click→open-lightbox listener that did not
           preventDefault — the click bubbled to the carousel card's <a>
           parent and the native link fired on the first click of any
           card. (Article-page carousel regression, 2026-05-12.) */
        if (img.closest('.sb-product-embed') ||
            img.closest('.sb-product-link') ||
            img.closest('.blog-carousel__card')) return;

        /* Find best source: srcset largest or src */
        var srcFull = img.src;
        if (img.srcset) {
          var parts = img.srcset.split(',');
          var best = 0;
          parts.forEach(function(part) {
            var trimmed = part.trim().split(/\s+/);
            if (trimmed.length >= 2) {
              var w = parseInt(trimmed[1]);
              if (w > best) { best = w; srcFull = trimmed[0]; }
            }
          });
        }

        self.images.push({
          src: img.src,
          srcFull: srcFull,
          alt: img.alt || '',
          element: img
        });

        img.setAttribute('data-gallery-index', idx);
        idx++;
      });
    },

    /* ─── Destroy cursor (for reinit) ─── */
    destroyCursor: function() {
      if (this.cursorEl) { this.cursorEl.remove(); this.cursorEl = null; }
    },

    /* ─── Lower-right offset spring follower with velocity-driven 3D tilt ──
       Target = mouse + (size/2, size/2) — icon sits to the lower-right
       of the mouse so the mouse rests at the icon's upper-left corner.
       Spring (ω≈14, ζ≈1.06) lags the mouse smoothly without overshoot.
       3D rotation is driven by spring velocity — banner tilts while
       moving and straightens when the mouse stops. Icon naturally
       extends past the viewport's right/bottom edges.
       Opacity is binary: full when the mouse is directly over a
       gallery image, zero otherwise. */
    initCursor: function() {
      /* Skip entirely on devices without a real hover-capable mouse
         (touch). Cursor stays active on desktop at any viewport width;
         the over-text fade is handled per-frame in the opacity logic. */
      if (window.matchMedia('(hover: none)').matches) return;
      if (window.matchMedia('(pointer: coarse)').matches) return;
      if ('ontouchstart' in window && navigator.maxTouchPoints > 0
          && !window.matchMedia('(hover: hover)').matches) return;

      var self = this;
      self.destroyCursor();

      var config = document.getElementById('cursor-config');
      if (!config) return;

      var iconUrl     = config.dataset.cursorIcon || '';
      var maxRotation = parseFloat(config.dataset.cursorRotation) || 60;
      var size        = parseInt(config.dataset.cursorSize)        || 180;
      var color       = config.dataset.cursorColor || '';

      /* Lower-right offset of icon centre from mouse. Setting it to
         size/2 puts the mouse at the icon's upper-left corner. */
      var offsetX = size * 0.5;
      var offsetY = size * 0.5;

      /* Spring tuning. ω = √STIFFNESS = √200 ≈ 14.1 rad/s, period ≈445ms.
         ζ = DAMPING / (2√STIFFNESS) = 30 / 28.3 ≈ 1.06 → barely
         overdamped: smooth lag, no overshoot. */
      var STIFFNESS    = 200;
      var DAMPING      = 30;
      var OPACITY_RATE = 9;
      /* Velocity → rotation gain. At ~1500 px/s, rotation ≈60° (clamped
         by maxRotation). Small movements give small tilts. */
      var VEL_TO_ROT   = 0.04;

      var cursor = document.createElement('div');
      cursor.className = 'sb-gallery-cursor';
      cursor.style.width  = size + 'px';
      cursor.style.height = size + 'px';
      if (color) cursor.style.color = color;

      if (iconUrl && iconUrl.indexOf('.svg') !== -1) {
        fetch(iconUrl).then(function(r) { return r.text(); }).then(function(svg) {
          cursor.innerHTML = svg;
          var el = cursor.querySelector('svg');
          if (el) { el.style.width = '100%'; el.style.height = '100%'; }
        }).catch(function() {});
      } else if (iconUrl) {
        cursor.innerHTML = '<img src="' + iconUrl + '" alt="" style="width:100%;height:100%;object-fit:contain;">';
      }

      document.body.appendChild(cursor);
      self.cursorEl = cursor;

      /* State */
      var rawX = null, rawY = null;
      var curX = 0, curY = 0;
      var velX = 0, velY = 0;
      var opacity = 0;
      var lastT = performance.now();

      document.addEventListener('mousemove', function(e) {
        if (rawX === null) {
          curX = e.clientX + offsetX;
          curY = e.clientY + offsetY;
        }
        rawX = e.clientX;
        rawY = e.clientY;
      }, { passive: true });

      function step(now) {
        var dt = (now - lastT) / 1000;
        lastT = now;
        if (dt > 0.05) dt = 0.05;

        if (rawX !== null) {
          var tx = rawX + offsetX;
          var ty = rawY + offsetY;
          var fx = STIFFNESS * (tx - curX) - DAMPING * velX;
          var fy = STIFFNESS * (ty - curY) - DAMPING * velY;
          velX += fx * dt;
          velY += fy * dt;
          curX += velX * dt;
          curY += velY * dt;
        }

        /* Opacity is binary on element identity, not distance. Visible
           if-and-only-if the mouse is directly over a gallery image.
           The earlier distance-based fade kept the cursor lit over
           text whenever an image happened to sit vertically nearby
           (the stacked-images case at narrow viewport widths). */
        var targetOpacity = 0;
        if (rawX !== null) {
          var elUnder = document.elementFromPoint(rawX, rawY);
          if (elUnder && elUnder.closest('[data-gallery-index]')) {
            targetOpacity = 1;
          }
        }
        /* Fade as the mouse approaches the LEFT or TOP viewport edge.
           Tight band — disappearance happens right at the page edge,
           not in the middle of the image. */
        var EDGE_BAND = 30;
        if (rawX !== null) {
          var edgeProx = Math.min(rawX, rawY);
          if (edgeProx < EDGE_BAND) {
            targetOpacity *= Math.max(0, edgeProx / EDGE_BAND);
          }
        }
        if (cursor.classList.contains('sb-gallery-cursor--suspended')) {
          targetOpacity = 0;
        }
        var opAlpha = 1 - Math.exp(-OPACITY_RATE * dt);
        opacity += (targetOpacity - opacity) * opAlpha;

        /* 3D rotation from velocity. velX → rotateY (banner tilts so its
           leading edge leans forward); velY → rotateX. Clamp to ±maxRotation. */
        var rotY = velX * VEL_TO_ROT;
        var rotX = -velY * VEL_TO_ROT;
        if (rotY >  maxRotation) rotY =  maxRotation;
        if (rotY < -maxRotation) rotY = -maxRotation;
        if (rotX >  maxRotation) rotX =  maxRotation;
        if (rotX < -maxRotation) rotX = -maxRotation;

        /* Edge-fade rotation: as the mouse approaches the left/top
           viewport edge, layer in a horizontal 3D flip (rotateX) — the
           bottom curls up toward the back like a flipping coin, in sync
           with the opacity fade. Same tight band as the fade. */
        if (rawX !== null) {
          if (rawX < EDGE_BAND) rotX += 90 * (1 - rawX / EDGE_BAND);
          if (rawY < EDGE_BAND) rotX += 90 * (1 - rawY / EDGE_BAND);
        }

        var half = size * 0.5;
        cursor.style.transform =
          'translate3d(' + (curX - half).toFixed(2) + 'px,' +
                          (curY - half).toFixed(2) + 'px,0) ' +
          'perspective(600px) ' +
          'rotateX(' + rotX.toFixed(2) + 'deg) ' +
          'rotateY(' + rotY.toFixed(2) + 'deg)';
        cursor.style.opacity = opacity.toFixed(3);

        requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    },

    /* ─── Click to open ─── */
    initClicks: function() {
      var self = this;
      self.images.forEach(function(item) {
        item.element.addEventListener('click', function() {
          var idx = parseInt(item.element.getAttribute('data-gallery-index'));
          self.open(idx);
        });
      });
    },

    /* ─── Open lightbox ─── */
    open: function(index) {
      var self = this;
      self.index = index;

      /* Build lightbox HTML — use article's page color as background */
      var pageColor = getComputedStyle(document.documentElement).getPropertyValue('--page-color').trim();
      var lb = document.createElement('div');
      lb.className = 'sb-gallery-lightbox';
      if (pageColor) lb.style.background = pageColor;
      lb.innerHTML =
        '<div class="sb-gallery__viewport">' +
          '<img class="sb-gallery__image" src="' + self.images[index].srcFull + '" alt="' + self.images[index].alt + '">' +
        '</div>' +
        '<button class="sb-gallery__close" aria-label="Close gallery">' +
          '<svg viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
        '</button>' +
        '<button class="sb-gallery__prev" aria-label="Previous image">' +
          '<svg viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
        '<button class="sb-gallery__next" aria-label="Next image">' +
          '<svg viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
        '<button class="sb-gallery__play" aria-label="Autoplay">' +
          '<svg viewBox="0 0 24 24" fill="none"><polygon points="5,3 19,12 5,21" fill="currentColor"/></svg>' +
        '</button>' +
        '<div class="sb-gallery__counter">' + (index + 1) + ' / ' + self.images.length + '</div>';

      document.body.appendChild(lb);
      self.lightbox = lb;
      self.viewport = lb.querySelector('.sb-gallery__viewport');
      self.img = lb.querySelector('.sb-gallery__image');
      self.counter = lb.querySelector('.sb-gallery__counter');

      /* Lock scroll */
      document.documentElement.style.overflow = 'hidden';

      /* Animate in */
      requestAnimationFrame(function() {
        lb.classList.add('sb-gallery-lightbox--open');
        requestAnimationFrame(function() {
          self.viewport.classList.add('sb-gallery--ready');
        });
      });

      /* Suspend follower while lightbox is open */
      if (self.cursorEl) self.cursorEl.classList.add('sb-gallery-cursor--suspended');

      self.bindEvents();
      self.initSwipe();
    },

    /* ─── Close ─── */
    close: function() {
      var self = this;
      self.stopPlay();

      if (self.lightbox) {
        self.lightbox.classList.remove('sb-gallery-lightbox--open');
        setTimeout(function() {
          if (self.lightbox && self.lightbox.parentNode) {
            self.lightbox.parentNode.removeChild(self.lightbox);
          }
          self.lightbox = null;
          self.viewport = null;
          self.img = null;
          self.counter = null;
        }, 600);
      }

      document.documentElement.style.overflow = '';
      document.removeEventListener('keydown', self._keyHandler);
      if (self.cursorEl) self.cursorEl.classList.remove('sb-gallery-cursor--suspended');
    },

    /* ─── Show image (crossfade) ─── */
    show: function(newIndex) {
      var self = this;
      if (!self.img || self.images.length === 0) return;

      /* Wrap around */
      if (newIndex < 0) newIndex = self.images.length - 1;
      if (newIndex >= self.images.length) newIndex = 0;
      self.index = newIndex;

      /* Crossfade out */
      self.img.style.transition = 'opacity 200ms ease, transform 200ms ease';
      self.img.style.opacity = '0';
      self.img.style.transform = 'scale(0.97)';

      setTimeout(function() {
        self.img.src = self.images[newIndex].srcFull;
        self.img.alt = self.images[newIndex].alt;
        self.img.onload = function() {
          self.img.style.transition = 'opacity 400ms cubic-bezier(0.16, 1, 0.3, 1), transform 400ms cubic-bezier(0.16, 1, 0.3, 1)';
          self.img.style.opacity = '1';
          self.img.style.transform = 'scale(1)';
        };
        /* Update counter */
        if (self.counter) {
          self.counter.textContent = (newIndex + 1) + ' / ' + self.images.length;
        }
      }, 200);
    },

    next: function() { this.show(this.index + 1); },
    prev: function() { this.show(this.index - 1); },

    /* ─── Autoplay ─── */
    togglePlay: function() {
      var self = this;
      if (self.playing) {
        self.stopPlay();
      } else {
        self.playing = true;
        var playBtn = self.lightbox && self.lightbox.querySelector('.sb-gallery__play');
        if (playBtn) playBtn.classList.add('sb-gallery__play--active');
        self.timer = setInterval(function() { self.next(); }, self.AUTOPLAY_MS);
      }
    },

    stopPlay: function() {
      this.playing = false;
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
      var playBtn = this.lightbox && this.lightbox.querySelector('.sb-gallery__play');
      if (playBtn) playBtn.classList.remove('sb-gallery__play--active');
    },

    /* ─── Bind events ─── */
    bindEvents: function() {
      var self = this;

      /* Button clicks */
      var closeBtn = self.lightbox.querySelector('.sb-gallery__close');
      var prevBtn = self.lightbox.querySelector('.sb-gallery__prev');
      var nextBtn = self.lightbox.querySelector('.sb-gallery__next');
      var playBtn = self.lightbox.querySelector('.sb-gallery__play');

      if (closeBtn) closeBtn.addEventListener('click', function() { self.close(); });
      if (prevBtn) prevBtn.addEventListener('click', function() { self.prev(); });
      if (nextBtn) nextBtn.addEventListener('click', function() { self.next(); });
      if (playBtn) playBtn.addEventListener('click', function() { self.togglePlay(); });

      /* Backdrop click */
      self.viewport.addEventListener('click', function(e) {
        if (e.target === self.viewport) self.close();
      });

      /* Keyboard. Detach any prior handler before assigning a new one
         so a re-open without a clean close() doesn't stack ESC handlers
         (rapid open-close-open used to multi-fire close on each ESC). */
      if (self._keyHandler) {
        document.removeEventListener('keydown', self._keyHandler);
      }
      self._keyHandler = function(e) {
        switch (e.keyCode) {
          case 27: self.close(); break;        /* ESC */
          case 37: self.prev(); break;         /* Left */
          case 39: self.next(); break;         /* Right */
          case 32: e.preventDefault(); self.togglePlay(); break; /* Space */
        }
      };
      document.addEventListener('keydown', self._keyHandler);
    },

    /* ─── Touch swipe ─── */
    initSwipe: function() {
      var self = this;
      if (!self.viewport) return;

      var startX = 0, deltaX = 0;

      self.viewport.addEventListener('touchstart', function(e) {
        startX = e.touches[0].clientX;
        deltaX = 0;
      }, { passive: true });

      self.viewport.addEventListener('touchmove', function(e) {
        deltaX = e.touches[0].clientX - startX;
        if (self.img) {
          self.img.style.transform = 'translateX(' + (deltaX * 0.3) + 'px)';
          self.img.style.transition = 'none';
        }
      }, { passive: true });

      self.viewport.addEventListener('touchend', function() {
        if (self.img) {
          self.img.style.transition = 'transform 300ms cubic-bezier(0.16, 1, 0.3, 1)';
          self.img.style.transform = 'translateX(0)';
        }
        if (Math.abs(deltaX) > 60) {
          if (deltaX < 0) self.next(); else self.prev();
        }
        deltaX = 0;
      });
    }
  };

  /* ─── Auto-init ─── */
  function initGallery() {
    if (document.querySelector('.blog-article__body')) {
      SbGallery.init();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGallery);
  } else {
    initGallery();
  }

  /* Reinit cursor when customizer reloads the article section */
  document.addEventListener('shopify:section:load', function(e) {
    if (e.target.querySelector('#cursor-config') || e.target.querySelector('.blog-article__body')) {
      SbGallery.collectImages();
      SbGallery.initCursor();
    }
  });
})();
